import React, {useContext, useEffect} from 'react'
import {Routes, useLocation} from "react-router"
import {AppContext} from "../context/app-context";
import Busy from "../components/Busy";
import ToastContainerWrapper from "../components/toast/ToastContainerWrapper";
import AccountContent from "./content-routes/AccountContent"
import AdminContent from "./content-routes/AdminContent"
import BuyerContent from "./content-routes/BuyerContent"
import GateManagementContent from "./content-routes/GateManagementContent"
import SupplierContent from "./content-routes/SupplierContent";
import LandingContent from "./content-routes/LandingContent";
import LoggedInAsHeader from "../components/admin/LoggedInAsHeader";
import Footer from "../components/footer/Footer";
import useHubSpotTracking from '../hooks/useHubSpotTracking';
import './style/secur-space-content.css'
import LocationUserBasedNav from './LocationUserBasedNav';
import {APIProvider} from "@vis.gl/react-google-maps";
import useInterval from '../hooks/useInterval';
import {ObservableVariables} from '../components/constants/observable-constants';
import {AssistantProvider} from '../context/assistant-context';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

//Note: this object is to gather the observables that this class needs. At present (Mar 2026) it
//      uses all existing observables, but that may not always be the case.
const observables = Object.freeze([
  ObservableVariables.PENDING_APPROVAL_COUNT,
  ObservableVariables.PENDING_INVENTORY_CORRECTIONS_COUNT,
  ObservableVariables.PENDING_INVENTORY_ACTIVITY_COUNT,
]);

const SecurSpaceContent: React.FC = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'pageview'
  });
  const appContext = useContext(AppContext);
  const location = useLocation();
  useHubSpotTracking();

  useEffect(() => {
    for (const observable of observables) {
      observable.refreshData(appContext);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext.user, appContext.observableMap]);

  for(const observable of observables) {
    // Note: this will cause a lint-error due to using useInterval in a loop, but in this case
    // it is intentional and should not be fixed. Because ObservableVariables is static and will
    // not change, we can be sure that the number of intervals will not change, and that we are
    // not violating any rules of hooks.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useInterval(() => {
      observable.refreshData(appContext);
    }, observable.pollingInterval(appContext));
  }

  // This fixes the scroll to Top issue.
  // Depends on `pathname`, not the whole `location`: report filters write every
  // keystroke into the query string, so a `location` dependency scrolled the
  // page to the top on each character typed.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  return <APIProvider apiKey={apiKey}
                      libraries={['places', 'geometry', 'geocoding']}
  >
    {/*
      Mounted here so the assistant survives navigation: it sits above the
      <Routes> tree, so moving between pages does not unmount it and lose a
      half-typed instruction. It renders nothing unless the user is signed in.
    */}
    <AssistantProvider>
      <div style={{
        position: "relative",
        height: "100%",
        width: "100%"
      }}>
        <div className="secur-space-content">
          <LoggedInAsHeader/>
          <LocationUserBasedNav
            locationPathname={location.pathname}
          />
          <div className="secur-space-page-content">
            <Routes>
              {AccountContent()}
              {AdminContent()}
              {BuyerContent()}
              {SupplierContent()}
              {GateManagementContent()}
              {LandingContent()}
            </Routes>
          </div>
          <Footer location={location}/>
        </div>
        <ToastContainerWrapper/>
        <Busy/>
      </div>
    </AssistantProvider>
  </APIProvider>
}

export default SecurSpaceContent
